import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Check, ArrowRight, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import Footer from '../components/Footer';
import FingerprintJS from '@fingerprintjs/fingerprintjs';

const PlanSelectionPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [paymentData, setPaymentData] = useState({
    transaction_id: '',
    transaction_date: '',
    payment_proof: null,
    coupon_code: ''
  });
  const [couponValidation, setCouponValidation] = useState(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [discountedFee, setDiscountedFee] = useState(null);
  const [deviceId, setDeviceId] = useState(null);

  useEffect(() => {
    fetchPricingPlans();
    initializeDeviceId();
  }, []);

  const initializeDeviceId = async () => {
    try {
      const fp = await FingerprintJS.load();
      const result = await fp.get();
      setDeviceId(result.visitorId);
      console.log('Device ID:', result.visitorId);
    } catch (error) {
      console.error('Failed to get device ID:', error);
      // Fallback to a random ID stored in localStorage
      let fallbackId = localStorage.getItem('deviceId');
      if (!fallbackId) {
        fallbackId = 'device_' + Math.random().toString(36).substring(2, 15);
        localStorage.setItem('deviceId', fallbackId);
      }
      setDeviceId(fallbackId);
    }
  };

  const fetchPricingPlans = async () => {
    try {
      const response = await axios.get(`${API}/pricing/plans`);
      setPlans(response.data.plans);
    } catch (error) {
      toast.error('Failed to load pricing plans');
    }
  };

  const handlePlanSelect = (plan) => {
    // Navigate directly to payment page with plan details
    navigate('/payment', {
      state: {
        plan_id: plan.plan_type,
        plan_price: plan.fee,
        plan_name: plan.name
      }
    });
  };

  const validateCoupon = async () => {
    if (!paymentData.coupon_code.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }

    if (!deviceId) {
      toast.error('Device ID not initialized. Please refresh the page.');
      return;
    }

    setValidatingCoupon(true);
    try {
      const couponCode = paymentData.coupon_code.toUpperCase().trim();
      
      // Check if it's the NEWUSER100 coupon
      if (couponCode === 'NEWUSER100') {
        const response = await axios.post(
          `${API}/coupons/validate`,
          {
            coupon_code: couponCode,
            device_id: deviceId
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.valid) {
          setCouponValidation(response.data);
          setDiscountedFee(0); // 100% off
          toast.success('🎉 ' + response.data.message);
        }
      } else {
        // Try old coupon validation endpoint for other coupons
        const response = await axios.post(`${API}/pricing/validate-coupon`, null, {
          params: {
            coupon_code: couponCode,
            plan_type: selectedPlan.plan_type
          },
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.valid) {
          setCouponValidation(response.data);
          setDiscountedFee(response.data.final_amount);
          toast.success(response.data.message);
        }
      }
    } catch (error) {
      setCouponValidation(null);
      setDiscountedFee(null);
      toast.error(error.response?.data?.detail || error.response?.data?.message || 'Failed to validate coupon');
    } finally {
      setValidatingCoupon(false);
    }
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    
    const finalAmount = discountedFee !== null ? discountedFee : selectedPlan.fee;
    const couponCode = paymentData.coupon_code ? paymentData.coupon_code.toUpperCase().trim() : '';
    
    // If final amount is 0 (100% discount), transaction ID is optional
    if (finalAmount > 0 && (!paymentData.transaction_id || !paymentData.transaction_date)) {
      toast.error('Please fill in all required payment fields');
      return;
    }

    setLoading(true);
    try {
      // First, create the fee record
      const response = await axios.post(
        `${API}/events/select-plan`,
        {
          plan_type: selectedPlan.plan_type,
          transaction_id: paymentData.transaction_id || 'COUPON_WAIVED',
          transaction_date: paymentData.transaction_date || new Date().toISOString().split('T')[0],
          payment_proof: paymentData.payment_proof,
          coupon_code: couponCode || null,
          device_id: deviceId
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // If NEWUSER100 coupon was validated and applied, call the apply endpoint
      if (couponCode === 'NEWUSER100' && couponValidation && finalAmount === 0) {
        try {
          await axios.post(
            `${API}/coupons/apply`,
            {
              coupon_code: couponCode,
              device_id: deviceId,
              fee_id: response.data.fee_id
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } catch (error) {
          console.error('Failed to record coupon usage:', error);
          // Don't block the flow, just log the error
        }
      }

      if (response.data.final_amount === 0 || finalAmount === 0) {
        toast.success('🎉 Coupon applied! Event fee waived. Your event will be instantly approved!', {
          description: 'No admin verification needed. Create your event now.',
          duration: 6000
        });
      } else {
        toast.success('Payment submitted! Please wait for admin verification.');
      }
      navigate('/create-event', { state: { fee_id: response.data.fee_id, isAutoApproved: response.data.final_amount === 0 || finalAmount === 0 } });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit payment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-contribo-navy/5 via-contribo-gold/5 to-blue-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <h1 className="text-3xl font-bold text-gray-900">Select Your Plan</h1>
          <p className="text-gray-600 mt-2">Choose the perfect plan for your event budget</p>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {!showPaymentForm ? (
          <>
            {/* Pricing Cards - Mobile Optimized 2x2 Grid */}
            <div className="grid grid-cols-2 gap-2 mb-8 px-2">
              {plans.map((plan) => (
                <Card
                  key={plan.plan_type}
                  className="relative hover:shadow-xl transition-shadow cursor-pointer border-2 hover:border-contribo-navy"
                  onClick={() => handlePlanSelect(plan)}
                >
                  <CardHeader className="text-center py-3 px-2">
                    <CardTitle className="text-sm font-bold text-contribo-navy">
                      {plan.name}
                    </CardTitle>
                    <div className="mt-2">
                      <span className="text-lg font-bold text-contribo-gold">₹{plan.fee}</span>
                    </div>
                  </CardHeader>
                  <CardContent className="text-center px-2 pb-3">
                    <div className="bg-green-50 rounded p-2 mb-2">
                      <p className="text-[10px] text-gray-700 font-semibold">Budget</p>
                      <p className="text-xs font-bold text-green-600">
                        ₹{plan.max_budget.toLocaleString()}
                      </p>
                    </div>
                    <div className="space-y-1 text-left mb-2">
                      <div className="flex items-center text-[10px] text-gray-700">
                        <Check className="h-3 w-3 text-green-600 mr-1" />
                        {plan.max_rsvps} RSVPs
                      </div>
                      <div className="flex items-center text-[10px] text-gray-700">
                        <Check className="h-3 w-3 text-green-600 mr-1" />
                        5 Goals
                      </div>
                      <div className="flex items-center text-[10px] text-gray-700">
                        <Check className="h-3 w-3 text-green-600 mr-1" />
                        Host & Co-Host
                      </div>
                      <div className="flex items-center text-[10px] text-gray-700">
                        <Check className="h-3 w-3 text-green-600 mr-1" />
                        100 MB photos
                      </div>
                      <div className="flex items-center text-[10px] text-gray-500 italic">
                        <span className="ml-4 text-[8px]">Auto delete after 96h from event time</span>
                      </div>
                    </div>
                    <Button className="w-full bg-contribo-navy hover:bg-contribo-navy/90 text-white text-xs py-2 h-auto">
                      Select
                      <ArrowRight className="ml-1 h-3 w-3" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Info Section */}
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">How It Works</h3>
              <ol className="list-decimal list-inside space-y-2 text-sm text-blue-800">
                <li>Select a plan based on your total event budget and RSVP limit</li>
                <li>Apply coupon code if you have one (CONTRIBO100 or CONTRIBO25P)</li>
                <li>Pay the fee to Contribo and submit transaction details</li>
                <li>Wait for verification: <strong>8 AM - 8 PM: 3-6 hours</strong> | <strong>8 PM - 8 AM: 6-10 hours</strong></li>
                <li>Create your event and start collecting contributions!</li>
              </ol>
            </div>
          </>
        ) : (
          /* Payment Form */
          <div className="max-w-2xl mx-auto">
            <Card className="border-2 border-contribo-navy">
              <CardHeader>
                <CardTitle className="text-2xl">Payment Details - {selectedPlan?.name}</CardTitle>
                <p className="text-gray-600">Complete your payment to continue</p>
              </CardHeader>
              <CardContent>
                {/* Coupon Section */}
                <div className="mb-6">
                  <Label htmlFor="coupon_code">Have a Coupon Code?</Label>
                  <div className="flex gap-2">
                    <Input
                      id="coupon_code"
                      placeholder="Enter CONTRIBO100 or CONTRIBO25P"
                      value={paymentData.coupon_code}
                      onChange={(e) => setPaymentData({...paymentData, coupon_code: e.target.value.toUpperCase()})}
                    />
                    <Button type="button" onClick={validateCoupon} disabled={validatingCoupon} variant="outline">
                      {validatingCoupon ? 'Validating...' : 'Apply'}
                    </Button>
                  </div>
                  {couponValidation && couponValidation.valid && (
                    <div className="mt-2 p-3 bg-green-50 border-2 border-green-200 rounded">
                      <p className="text-sm font-semibold text-green-800">✓ Coupon Applied!</p>
                      <p className="text-xs text-green-700 mt-1">
                        Original: ₹{couponValidation.original_amount} | 
                        Discount: -₹{couponValidation.discount_amount} | 
                        <strong> Final: ₹{couponValidation.final_amount}</strong>
                      </p>
                    </div>
                  )}
                </div>

                {/* Payment Amount Display */}
                <div className="bg-contribo-navy text-white rounded-lg p-6 mb-6">
                  <div className="flex justify-between items-center">
                    <span className="text-lg">Amount to Pay:</span>
                    <div className="text-right">
                      {discountedFee !== null && discountedFee < selectedPlan?.fee && (
                        <p className="text-sm line-through text-gray-300">₹{selectedPlan?.fee}</p>
                      )}
                      <span className="text-3xl font-bold text-contribo-gold">
                        ₹{discountedFee !== null ? discountedFee : selectedPlan?.fee}
                      </span>
                    </div>
                  </div>
                </div>

                {discountedFee === 0 ? (
                  <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4 mb-4">
                    <p className="text-green-800 font-semibold">🎉 Your fee is fully waived with coupon!</p>
                    <p className="text-sm text-green-700 mt-1">Click "Proceed" to create your event.</p>
                  </div>
                ) : (
                  <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-blue-800 mb-2">
                      <strong>💳 Payment Instructions:</strong>
                    </p>
                    <p className="text-sm text-blue-700">
                      Pay <strong>₹{discountedFee !== null ? discountedFee : selectedPlan?.fee}</strong> to Contribo UPI and enter transaction details below.
                    </p>
                  </div>
                )}

                <form onSubmit={handlePaymentSubmit} className="space-y-4">
                  {(discountedFee === null || discountedFee > 0) && (
                    <>
                      <div>
                        <Label htmlFor="transaction_id">Transaction ID *</Label>
                        <Input
                          id="transaction_id"
                          placeholder="Enter UPI transaction ID"
                          value={paymentData.transaction_id}
                          onChange={(e) => setPaymentData({...paymentData, transaction_id: e.target.value})}
                          required={discountedFee !== 0}
                        />
                      </div>

                      <div>
                        <Label htmlFor="transaction_date">Transaction Date *</Label>
                        <Input
                          id="transaction_date"
                          type="date"
                          value={paymentData.transaction_date}
                          onChange={(e) => setPaymentData({...paymentData, transaction_date: e.target.value})}
                          required={discountedFee !== 0}
                        />
                      </div>
                    </>
                  )}

                  <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4">
                    <p className="text-sm text-yellow-800">
                      <strong>⏱️ Verification Time:</strong><br/>
                      • 8 AM - 8 PM: 3-6 hours<br/>
                      • 8 PM - 8 AM: 6-10 hours<br/>
                      You can create your event, but sharing will be enabled only after verification.
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowPaymentForm(false);
                        setCouponValidation(null);
                        setDiscountedFee(null);
                        setPaymentData({transaction_id: '', transaction_date: '', payment_proof: null, coupon_code: ''});
                      }}
                      className="flex-1"
                    >
                      Back
                    </Button>
                    <Button
                      type="submit"
                      disabled={loading}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                    >
                      <CreditCard className="mr-2 h-4 w-4" />
                      {loading ? 'Submitting...' : (discountedFee === 0 ? 'Proceed' : 'Submit Payment')}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default PlanSelectionPage;
